///
/// EarthBrowser - Location/Cities Browser
///
/// Paginated implementation for large location datasets
/// Copyright (c) 2025 Geodata Labs
///

import { DatasetBrowser, type DatasetBrowserColumn } from "@/components/data-browsers/DatasetBrowser"
import { type Database } from "@/earthbrowser/database/Database"
import { Landmark } from "lucide-react"
import { useEffect, useState, useCallback, useRef } from "react"

export interface LocationData extends Record<string, unknown> {
	geoid: number
	name: string
	admin: string
	country: string
	pop: number
	alt: number
}

interface LocationBrowserProps {
	onLocationSelect: (location: LocationData) => void
	database: Database | null
	countries: Record<string, string>
	admins: Record<string, Record<string, string>>
	embeddedInWindow?: boolean
}

const PAGE_SIZE = 50 // Items per page
const SEARCH_LIMIT = 100 // Max search results
const MIN_SEARCH_LENGTH = 2 // Minimum characters before searching
const SEARCH_DEBOUNCE = 300 // Debounce delay in ms

type LocationRow = { geoid: number, name: string, admin: string, country: string, pop: number, alt: number }

export function LocationBrowser({
	onLocationSelect,
	database,
	countries,
	admins,
	embeddedInWindow = false,
}: LocationBrowserProps) {
	const [locations, setLocations] = useState<LocationData[]>([])
	const [isLoading, setIsLoading] = useState(false)
	const [searchQuery, setSearchQuery] = useState('')
	const [currentPage, setCurrentPage] = useState(0)
	const [totalCount, setTotalCount] = useState(0)
	const [isSearchMode, setIsSearchMode] = useState(false)
	const loadingRef = useRef(false)

	const totalPages = Math.ceil(totalCount / PAGE_SIZE)
	const hasNextPage = currentPage < totalPages - 1
	const hasPrevPage = currentPage > 0

	const transformLocation = useCallback((loc: LocationRow): LocationData => {
		const adminFromCntAdm = (cnt: string, adm: string) => {
			const cntadm = admins[cnt]
			if (cntadm) {
				return cntadm[adm]
			}
			return ""
		}

		return {
			geoid: loc.geoid,
			name: loc.name,
			admin: adminFromCntAdm(loc.country, loc.admin),
			country: countries[loc.country] || loc.country,
			pop: loc.pop,
			alt: loc.alt
		}
	}, [admins, countries])

	const totalCountRef = useRef(0)

	const loadPage = useCallback(async (page: number) => {
		if (!database) {
			console.warn("[LocationBrowser] loadPage: database not available")
			return
		}
		if (loadingRef.current) {
			console.warn("[LocationBrowser] loadPage: already loading")
			return
		}

		loadingRef.current = true
		setIsLoading(true)

		try {
			if (totalCountRef.current === 0) {
				const countResult = await database.exec(
					"SELECT COUNT(*) as count FROM locations",
					[],
					"high"
				) as { count: number }[]
				const count = countResult[0]?.count ?? 0
				console.log("[LocationBrowser] Total locations count:", count)
				if (count === 0) {
					console.warn("[LocationBrowser] No locations in database - is the locations package installed?")
				}
				totalCountRef.current = count
				setTotalCount(count)
			}

			const offset = page * PAGE_SIZE
			const query = `SELECT geoid, name, country, admin1, admin2, pop, alt, code
				FROM locations
				ORDER BY pop DESC
				LIMIT ${PAGE_SIZE} OFFSET ${offset}`

			const locs = await database.exec(query, [], "critical") as LocationRow[]
			console.log("[LocationBrowser] Loaded", locs.length, "locations for page", page)
			const data = locs.map(transformLocation)
			setLocations(data)
			setCurrentPage(page)
		} catch (error) {
			console.error("[LocationBrowser] Failed to load locations page:", error)
		} finally {
			setIsLoading(false)
			loadingRef.current = false
		}
	}, [database, transformLocation])

	const searchLocations = useCallback(async (term: string) => {
		if (!database) {
			console.warn("[LocationBrowser] searchLocations: database not available")
			return
		}
		if (loadingRef.current) {
			console.warn("[LocationBrowser] searchLocations: already loading")
			return
		}

		loadingRef.current = true
		setIsLoading(true)

		try {
			const containsPattern = `%${term}%`
			const startsWithPattern = `${term}%`

			const query = `SELECT geoid, name, country, admin1, admin2, pop, alt, code FROM locations
				WHERE name LIKE ?
				ORDER BY
					CASE WHEN name LIKE ? THEN 0 ELSE 1 END,
					pop DESC
				LIMIT ${SEARCH_LIMIT}`

			const locs = await database.exec(
				query,
				[containsPattern, startsWithPattern],
				"critical"
			) as LocationRow[]

			console.log("[LocationBrowser] Search for", term, "found", locs.length, "results")
			const data = locs.map(transformLocation)
			setLocations(data)
			setIsSearchMode(true)
		} catch (error) {
			console.error("[LocationBrowser] Failed to search locations:", error)
		} finally {
			setIsLoading(false)
			loadingRef.current = false
		}
	}, [database, transformLocation])

	// Load first page as soon as database is available (don't wait for countries)
	useEffect(() => {
		if (!database) return
		if (isSearchMode) return

		void loadPage(currentPage)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [database, currentPage, isSearchMode])

	// Re-transform displayed locations when countries/admins finish loading
	const countriesReady = Object.keys(countries).length > 0
	useEffect(() => {
		if (!countriesReady || locations.length === 0) return
		// Countries just loaded - re-transform to show proper names
		setLocations(prev => prev.map(loc => ({
			...loc,
			country: countries[loc.country] || loc.country,
		})))
	}, [countriesReady]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (!database) return

		if (!searchQuery || searchQuery.length < MIN_SEARCH_LENGTH) {
			if (isSearchMode) {
				setIsSearchMode(false)
				setCurrentPage(0)
				void loadPage(0)
			}
			return
		}

		const timeoutId = setTimeout(() => {
			void searchLocations(searchQuery)
		}, SEARCH_DEBOUNCE)

		return () => clearTimeout(timeoutId)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchQuery, database, isSearchMode])

	const goToNextPage = useCallback(() => {
		if (hasNextPage && !isSearchMode) {
			void loadPage(currentPage + 1)
		}
	}, [hasNextPage, isSearchMode, currentPage, loadPage])

	const goToPrevPage = useCallback(() => {
		if (hasPrevPage && !isSearchMode) {
			void loadPage(currentPage - 1)
		}
	}, [hasPrevPage, isSearchMode, currentPage, loadPage])

	const goToFirstPage = useCallback(() => {
		if (!isSearchMode && currentPage !== 0) {
			void loadPage(0)
		}
	}, [isSearchMode, currentPage, loadPage])

	const goToLastPage = useCallback(() => {
		if (!isSearchMode && currentPage !== totalPages - 1) {
			void loadPage(totalPages - 1)
		}
	}, [isSearchMode, currentPage, totalPages, loadPage])

	const columns: DatasetBrowserColumn<LocationData>[] = [
		{
			id: 'name',
			header: 'City',
			accessorKey: 'name',
			sortable: true,
			minWidth: 200,
			cell: (value) => (
				<div className="font-medium">{value as string}</div>
			)
		},
		{
			id: 'admin',
			header: 'Region',
			accessorKey: 'admin',
			sortable: true,
			minWidth: 150,
			cell: (value) => (
				<span className="text-white/80">{(value as string) || '—'}</span>
			)
		},
		{
			id: 'country',
			header: 'Country',
			accessorKey: 'country',
			sortable: true,
			minWidth: 150
		},
		{
			id: 'pop',
			header: 'Population',
			accessorKey: 'pop',
			sortable: true,
			minWidth: 120,
			cell: (value) => {
				const pop = value as number
				if (pop >= 1000000) {
					return (
						<span className="text-cyan-400/80">
							{(pop / 1000000).toFixed(1)}M
						</span>
					)
				}
				if (pop >= 1000) {
					return (
						<span className="text-cyan-400/80">
							{(pop / 1000).toFixed(0)}K
						</span>
					)
				}
				return (
					<span className="text-cyan-400/80">
						{pop.toLocaleString()}
					</span>
				)
			}
		},
		{
			id: 'alt',
			header: 'Altitude',
			accessorKey: 'alt',
			sortable: true,
			minWidth: 100,
			cell: (value) => {
				const alt = value as number
				if (alt === 0) return <span className="text-white/40">—</span>
				return (
					<span className="text-green-400/80">
						{alt > 0 ? `${alt}m` : `${Math.abs(alt)}m (below)`}
					</span>
				)
			}
		}
	]

	const handleSearchChange = useCallback((query: string) => {
		setSearchQuery(query)
	}, [])

	const noopClose = useCallback(() => {}, [])

	return (
		<DatasetBrowser
			isOpen
			onClose={noopClose}
			title="Cities & Locations"
			data={locations}
			columns={columns}
			onRowClick={onLocationSelect}
			searchPlaceholder={`Search cities (min ${MIN_SEARCH_LENGTH} chars)...`}
			isLoading={isLoading}
			icon={<Landmark className="w-5 h-5" />}
			iconColor="text-blue-400"
			onSearchChange={handleSearchChange}
			resultsInfo={{
				currentCount: locations.length,
				totalCount,
				isSearchMode
			}}
			pagination={!isSearchMode ? {
				currentPage,
				totalPages,
				hasNextPage,
				hasPrevPage,
				onNextPage: goToNextPage,
				onPrevPage: goToPrevPage,
				onFirstPage: goToFirstPage,
				onLastPage: goToLastPage,
			} : undefined}
			closeOnRowClick={false}
			embeddedInWindow={embeddedInWindow}
			searchNotifyMinLength={MIN_SEARCH_LENGTH}
			className={embeddedInWindow ? "min-h-0 flex-1 rounded-none border-0 shadow-none bg-black/40" : undefined}
		/>
	)
}
